import { Injectable } from '@angular/core'
import { CourtroomRealTimeSttConfigurationService, RegionalPermissionService } from '@ftr/api-regional'
import { UserGroupPermissionId } from '@ftr/contracts/api/user-group'
import { assert } from '@ftr/contracts/shared/Assertions'
import { Uuid } from '@ftr/contracts/type/shared'
import { RealTimePlaybackKey } from '@ftr/data-realtime-playback'
import { ApiResult, RemoteData, TrackingService, assertUnreachable, ignoreErrors, unwrapData } from '@ftr/foundation'
import { LoggingService } from '@ftr/ui-observability'
import { AuthenticationService } from '@ftr/ui-user'
import { Action, Selector, State, StateContext, createSelector } from '@ngxs/store'
import { patch, ɵPatchSpec } from '@ngxs/store/operators'
import { memoize } from 'lodash-es'
import { EMPTY, catchError, firstValueFrom, lastValueFrom, map, switchMap, takeUntil } from 'rxjs'
import {
  RealTimeAudioSegmentSttService,
  RealTimeCourtRecordingSttService,
  RegionalHearingsService,
} from '../../services'
import { RealTimeSttSegmentMap, RealTimeSttSpeaker, RealTimeSttWithRemarks } from '../../types'
import {
  RealTimeLiveSttService,
  buildJoinLiveStreamRoomRequest,
} from '../real-time-live-stt/real-time-live-stt.service'
import { sttEditOperationToTransaction } from './edit-operations'
import {
  ApplySttEditTransactionAction,
  FetchCourtroomSttModeAction,
  FetchRealTimeSttCanSealContentAction,
  FetchRealTimeSttSegmentMapAction,
  HighlightRealTimeSttTranscriptElementsAtTimestamp,
  InvokeSttEditOperationSideEffectAction,
  JumpRealTimeSttToTranscriptElementAction,
  PerformSttEditOperationAction,
  PersistSttEditTransactionAction,
  QueuePersistSttEditTransactionAction,
  SetHideAfterAction,
  SetRealTimeSttFailedToSetupConnectionAction,
  SetRealTimeSttIsLivePinnedAction,
  SetRealTimeSttSourceStateAction,
  StopRealTimeSttSegmentMapLiveUpdatesAction,
  StreamRealTimeSttSegmentMapLiveUpdatesAction,
  TriggerSttEditPersistQueueProcessingAction,
  UpdateRealTimeSttSourceStateSegmentMapAction,
  UpsertRealTimeSttLiveRemarksAction,
} from './real-time-stt.actions'
import {
  FocusEditableRemarkCommand,
  RealTimeSttSetSearchHighlightsCommand,
  SetRealTimeRecordingEndTimeCommand,
  SetRealTimeSttCurrentPlaybackTimeCommand,
  SetRealTimeSttHearingMarkersCommand,
  SetRealTimeSttSealingMarkersCommand,
  SetRealTimeSttSessionMarkersCommand,
  ShowValidationErrorRemarkCommand,
  UpdateRealTimeLiveLocalTimeCommand,
} from './real-time-stt.commands'
import {
  PersistSttEditTransactionBeganEvent,
  PersistSttEditTransactionFailedEvent,
  PersistSttEditTransactionSucceededEvent,
  RealTimeSttLastEditedUpdatedEvent,
  RealTimeSttSegmentMapReinitializedEvent,
  SttEditOperationFailedEvent,
  SttEditOperationSkippedEvent,
  SttEditOperationSucceededEvent,
} from './real-time-stt.events'
import { RealTimeSttSourceStateModel, RealTimeSttStateModel } from './real-time-stt.model'

export function defaultRealTimeSttState(): RealTimeSttStateModel {
  return {
    sttSourceState: {},
  }
}

export function defaultSourceState(): RealTimeSttSourceStateModel {
  return {
    isLivePinned: false,
    segmentMap: RemoteData.notAsked(),
    highlightedTranscriptElementIds: [],
    playbackMarkerTranscriptElementIds: [],
    courtroomSttMode: 'off',
    canSealContent: false,
    canReadStt: false,
    isStreamingLive: false,
    stopLiveStreamingHandle: undefined,
    failedRealTimeSttConnection: false,
    currentPlaybackTime: undefined,
    recordingEndTime: undefined,
    liveLocalTime: undefined,
    editPersistQueue: {
      persistInProgress: false,
      queuedJobs: [],
    },
  }
}

@State<RealTimeSttStateModel>({
  name: 'realTimeSttState',
  defaults: defaultRealTimeSttState(),
})
@Injectable()
export class RealTimeSttState {
  @Selector()
  static allSttSourceStates(state: RealTimeSttStateModel): RealTimeSttStateModel['sttSourceState'] {
    return state.sttSourceState
  }

  static readonly sttSourceState = memoize(
    (
      playbackKey: RealTimePlaybackKey,
    ): ((
      sourceStates: ReturnType<typeof RealTimeSttState.allSttSourceStates>,
    ) => RealTimeSttSourceStateModel | undefined) => {
      return createSelector(
        [RealTimeSttState.allSttSourceStates],
        (sourceStates: ReturnType<typeof RealTimeSttState.allSttSourceStates>) => sourceStates[getSttId(playbackKey)],
      )
    },
    playbackKey => getSttId(playbackKey),
  )

  static readonly sttSpeakers = memoize(
    (playbackKey: RealTimePlaybackKey): ((sourceStates: RealTimeSttSourceStateModel) => RealTimeSttSpeaker[]) => {
      const selectedSourceState = RealTimeSttState.sttSourceState(playbackKey)
      return createSelector([selectedSourceState], (state: ReturnType<typeof selectedSourceState>) => {
        const segmentMap = state?.segmentMap
        if (!segmentMap?.isSuccess()) {
          return []
        }

        const speakers: RealTimeSttSpeaker[] = []
        for (const segment of segmentMap._data.segments) {
          for (const transcriptElement of segment.transcriptElements) {
            if (
              transcriptElement.type === 'Remark' &&
              transcriptElement.speakerName &&
              !speakers.some(x => x.speakerId === transcriptElement.speakerId)
            ) {
              speakers.push({
                speakerName: transcriptElement.speakerName,
                speakerId: transcriptElement.speakerId,
              })
            }
          }
        }

        return speakers
      })
    },
    playbackKey => getSttId(playbackKey),
  )

  static readonly sttSpeakerNames = memoize(
    (playbackKey: RealTimePlaybackKey): ((sourceStates: RealTimeSttSpeaker[]) => string[]) => {
      const speakers = RealTimeSttState.sttSpeakers(playbackKey)
      return createSelector([speakers], (state: ReturnType<typeof speakers>) => {
        return Array.from(new Set(state.map(x => x.speakerName)))
      })
    },
    playbackKey => getSttId(playbackKey),
  )

  static readonly sttSegmentMap = createMemoizedSttSourceStateSelector('segmentMap')

  static readonly sttIsLivePinned = createMemoizedSttSourceStateSelector('isLivePinned')

  static readonly highlightedRemarkIds = createMemoizedSttSourceStateSelector('highlightedTranscriptElementIds')

  static readonly playbackMarkerTranscriptElementIds = createMemoizedSttSourceStateSelector(
    'playbackMarkerTranscriptElementIds',
  )

  static readonly courtroomSttMode = createMemoizedSttSourceStateSelector('courtroomSttMode')

  static readonly canSealContent = createMemoizedSttSourceStateSelector('canSealContent')

  static readonly failedRealTimeSttConnection = createMemoizedSttSourceStateSelector('failedRealTimeSttConnection')

  static readonly recordingEndTime = createMemoizedSttSourceStateSelector('recordingEndTime')

  static readonly liveLocalTime = createMemoizedSttSourceStateSelector('liveLocalTime')

  constructor(
    private readonly courtroomRealTimeSttConfigurationService: CourtroomRealTimeSttConfigurationService,
    private readonly regionalPermissionService: RegionalPermissionService,
    private readonly realTimeAudioSegmentSttService: RealTimeAudioSegmentSttService,
    private readonly realTimeCourtRecordingSttService: RealTimeCourtRecordingSttService,
    private readonly liveSttService: RealTimeLiveSttService,
    private readonly authService: AuthenticationService,
    private readonly regionalHearingsService: RegionalHearingsService,
    private readonly trackingService: TrackingService,
    private readonly logger: LoggingService,
  ) {}

  @Action(SetRealTimeSttSourceStateAction)
  setSttSourceState(
    { setState }: StateContext<RealTimeSttStateModel>,
    { playbackKey, state }: SetRealTimeSttSourceStateAction,
  ): void {
    const sttId = getSttId(playbackKey)
    setState(
      patch<RealTimeSttStateModel>({
        sttSourceState: patch({
          [sttId]: state,
        }),
      }),
    )
  }

  @Action(UpdateRealTimeSttSourceStateSegmentMapAction)
  async updateRealTimeSttSourceStateSegmentMap(
    { setState, getState }: StateContext<RealTimeSttStateModel>,
    { playbackKey, segmentMap }: UpdateRealTimeSttSourceStateSegmentMapAction,
  ): Promise<void> {
    const sttId = getSttId(playbackKey)
    this.getOrCreateSourceState(sttId, getState, setState)

    const segmentMapRemoteData: RemoteData<RealTimeSttSegmentMap> = RemoteData.success(segmentMap)

    patchSttSourceState(setState, sttId, { segmentMap: segmentMapRemoteData })
  }

  @Action(FetchRealTimeSttSegmentMapAction)
  async fetchRealTimeSttSegmentMap(
    { setState, getState, dispatch }: StateContext<RealTimeSttStateModel>,
    {
      playbackKey,
      courtSystemId,
      recordingStartTime,
      sttConfiguration: { pauseConfiguration, canReadStt, hideAfter },
    }: FetchRealTimeSttSegmentMapAction,
  ): Promise<void> {
    const sttId = getSttId(playbackKey)
    this.getOrCreateSourceState(sttId, getState, setState)

    if (getState().sttSourceState[sttId]?.segmentMap.isLoading()) {
      return
    }

    patchSttSourceState(setState, sttId, { segmentMap: RemoteData.loading() })

    let fetchedRemarks
    try {
      fetchedRemarks = await lastValueFrom(this.getRemarks(playbackKey, courtSystemId, canReadStt).pipe(unwrapData()))
    } catch (error) {
      patchSttSourceState(setState, sttId, { segmentMap: RemoteData.failure(error as Error) })
      return
    }

    const segmentMap = RealTimeSttSegmentMap.createSegmentMapWithFullDayOfSegments(
      recordingStartTime,
      pauseConfiguration,
      fetchedRemarks.remarks,
      {
        selected: undefined,
        hovered: undefined,
      },
      hideAfter,
    )
    patchSttSourceState(setState, sttId, { segmentMap: RemoteData.success(segmentMap), canReadStt })

    dispatch([
      new RealTimeSttSegmentMapReinitializedEvent(playbackKey),
      new RealTimeSttLastEditedUpdatedEvent(playbackKey, fetchedRemarks.lastEdited),
    ])
  }

  @Action(SetRealTimeSttSessionMarkersCommand)
  async setRealTimeSttSessionMarkers(
    { setState, getState }: StateContext<RealTimeSttStateModel>,
    { playbackKey, sessionMarkers }: SetRealTimeSttSessionMarkersCommand,
  ): Promise<void> {
    const sttId = getSttId(playbackKey)
    this.getOrCreateSourceState(sttId, getState, setState)

    const segmentMap = getState().sttSourceState[sttId]?.segmentMap
    if (!segmentMap?.isSuccess()) {
      // Can't update markers if we don't have a segment map
      return
    }

    const updatedSegmentMap = segmentMap._data.rebuildWithSessionMarkers(sessionMarkers)

    patchSttSourceState(setState, sttId, { segmentMap: RemoteData.success(updatedSegmentMap) })
  }

  @Action(SetRealTimeSttSealingMarkersCommand)
  async setRealTimeSttSealingMarkers(
    { setState, getState }: StateContext<RealTimeSttStateModel>,
    { playbackKey, sealingMarkers }: SetRealTimeSttSealingMarkersCommand,
  ): Promise<void> {
    const sttId = getSttId(playbackKey)
    this.getOrCreateSourceState(sttId, getState, setState)

    const segmentMap = getState().sttSourceState[sttId]?.segmentMap
    if (!segmentMap?.isSuccess()) {
      // Can't update markers if we don't have a segment map
      return
    }

    const updatedSegmentMap = segmentMap._data.rebuildWithSealingMarkers(sealingMarkers)

    patchSttSourceState(setState, sttId, { segmentMap: RemoteData.success(updatedSegmentMap) })
  }

  @Action(StreamRealTimeSttSegmentMapLiveUpdatesAction)
  async streamRealTimeSttSegmentMapLiveUpdates(
    { setState, getState, dispatch }: StateContext<RealTimeSttStateModel>,
    { playbackKey, courtSystemId, region, canAccessSealedContent }: StreamRealTimeSttSegmentMapLiveUpdatesAction,
  ): Promise<void> {
    const sttId = getSttId(playbackKey)
    const originalState = this.getOrCreateSourceState(sttId, getState, setState)
    if (originalState && originalState.isStreamingLive) {
      // Don't want to double stream updates
      return
    }

    patchSttSourceState(setState, sttId, {
      isStreamingLive: true,
      failedRealTimeSttConnection: false,
    })

    const shouldStopLiveStreaming = new Promise<void>(resolve => {
      patchSttSourceState(setState, sttId, {
        stopLiveStreamingHandle: {
          stopStreaming: () => {
            resolve()
          },
        },
      })
    })

    const joinRoomRequest = buildJoinLiveStreamRoomRequest(
      playbackKey,
      courtSystemId,
      region,
      canAccessSealedContent,
      await this.authService.currentJwtToken,
    )

    if (!joinRoomRequest) {
      return
    }

    const onJoinRoomFailed: () => void = () => dispatch(new SetRealTimeSttFailedToSetupConnectionAction(playbackKey))

    this.liveSttService
      .observeLiveSttResults(joinRoomRequest, onJoinRoomFailed)
      .pipe(
        ignoreErrors(), // So if anything above crashes, we continue receiving socket events
        switchMap(update => dispatch(new UpsertRealTimeSttLiveRemarksAction(playbackKey, update))),
        takeUntil(shouldStopLiveStreaming),
      )
      .subscribe()

    this.liveSttService
      .observeSttEdits(joinRoomRequest, onJoinRoomFailed)
      .pipe(
        catchError(error => {
          this.logger.error(new Error('Error observing SttEdit Transaction', { cause: error }))
          return EMPTY
        }),
        // we don't care about softCreates coming in over socket
        map(({ editTime, transaction }) => ({
          editTime,
          transaction: {
            ...transaction,
            edits: transaction.edits.filter(edit => edit.type !== 'softCreateUtterance'),
          },
        })),
        switchMap(({ editTime, transaction }) =>
          dispatch([
            new RealTimeSttLastEditedUpdatedEvent(playbackKey, editTime),
            new ApplySttEditTransactionAction(playbackKey, transaction),
          ]),
        ),
        takeUntil(shouldStopLiveStreaming),
      )
      .subscribe()
  }

  @Action(UpsertRealTimeSttLiveRemarksAction)
  async upsertRealTimeSttLiveRemarks(
    { setState, getState }: StateContext<RealTimeSttStateModel>,
    { playbackKey, update }: UpsertRealTimeSttLiveRemarksAction,
  ): Promise<void> {
    const sttId = getSttId(playbackKey)
    this.getOrCreateSourceState(sttId, getState, setState)

    const segmentMapRemote = getState().sttSourceState[sttId]?.segmentMap

    if (!segmentMapRemote || !segmentMapRemote.isSuccess()) {
      // Can't update remarks if we don't have a segment map
      return
    }
    const segmentMap = segmentMapRemote._data.processLiveUpdate(update)

    patchSttSourceState(setState, sttId, { segmentMap: RemoteData.success(segmentMap) })
  }

  @Action(StopRealTimeSttSegmentMapLiveUpdatesAction)
  async stopRealTimeSttSegmentMapLiveUpdates(
    { getState, setState }: StateContext<RealTimeSttStateModel>,
    { playbackKey }: StopRealTimeSttSegmentMapLiveUpdatesAction,
  ): Promise<void> {
    const sttId = getSttId(playbackKey)
    const originalState = getState().sttSourceState[sttId]
    if (!originalState?.isStreamingLive) {
      // We weren't streaming
      return
    }
    if (originalState.stopLiveStreamingHandle) {
      originalState.stopLiveStreamingHandle.stopStreaming()
    }
    patchSttSourceState(setState, sttId, { isStreamingLive: false, stopLiveStreamingHandle: undefined })
  }

  @Action(SetRealTimeSttIsLivePinnedAction)
  async setSttIsLivePinned(
    { setState, getState }: StateContext<RealTimeSttStateModel>,
    { playbackKey, isLivePinned }: SetRealTimeSttIsLivePinnedAction,
  ): Promise<void> {
    const sttId = getSttId(playbackKey)
    this.getOrCreateSourceState(sttId, getState, setState)

    patchSttSourceState(setState, sttId, { isLivePinned })
  }

  @Action(SetRealTimeSttCurrentPlaybackTimeCommand)
  async setSttCurrentPlaybackTime(
    { setState, getState }: StateContext<RealTimeSttStateModel>,
    { playbackKey, currentPlaybackTime }: SetRealTimeSttCurrentPlaybackTimeCommand,
  ): Promise<void> {
    const sttId = getSttId(playbackKey)
    this.getOrCreateSourceState(sttId, getState, setState)

    patchSttSourceState(setState, sttId, { currentPlaybackTime })
  }

  @Action(SetHideAfterAction)
  async setHideAfter(
    { setState, getState }: StateContext<RealTimeSttStateModel>,
    { playbackKey, hideAfter }: SetHideAfterAction,
  ): Promise<void> {
    const sttId = getSttId(playbackKey)
    const originalState = getState().sttSourceState[sttId]
    if (!originalState || !originalState.segmentMap.isSuccess()) {
      return
    }
    const segmentMap = originalState.segmentMap._data

    patchSttSourceState(setState, sttId, {
      segmentMap: RemoteData.success(segmentMap.withUpdatedHideAfter(hideAfter)),
    })
  }

  @Action(HighlightRealTimeSttTranscriptElementsAtTimestamp)
  setHighlightedRemarks(
    { setState, getState }: StateContext<RealTimeSttStateModel>,
    { playbackKey, timestamp, playbackMarkerHighlightType }: HighlightRealTimeSttTranscriptElementsAtTimestamp,
  ): void {
    const sttId = getSttId(playbackKey)
    const originalState = getState().sttSourceState[sttId]
    if (!originalState || !originalState.segmentMap.isSuccess()) {
      // We can't highlight remarks which don't exist 🧠
      return
    }
    const segmentMap = originalState.segmentMap._data

    const remarksToHighlight = segmentMap.getRemarksAt(timestamp) ?? []

    const pauseAtTime = segmentMap.getPauseAt(timestamp)

    // Pauses are not highlighted in the designs - they only include the playback marker
    const highlightedTranscriptElementIds = remarksToHighlight.map(({ id }) => id)
    const playbackMarkerTranscriptElementIds = remarksToHighlight.map(remark => {
      switch (playbackMarkerHighlightType) {
        case 'firstInGroup':
          return remark.firstRemarkIdInGroup
        case 'remark':
          return remark.id
        default:
          assertUnreachable(playbackMarkerHighlightType)
      }
    })
    if (pauseAtTime) {
      playbackMarkerTranscriptElementIds.push(pauseAtTime.id)
    }

    patchSttSourceState(setState, sttId, {
      highlightedTranscriptElementIds,
      playbackMarkerTranscriptElementIds,
    })
  }

  @Action(FetchCourtroomSttModeAction)
  async fetchRealTimeSttEnabled(
    { setState, getState }: StateContext<RealTimeSttStateModel>,
    { playbackKey, courtSystemId, locationId }: FetchCourtroomSttModeAction,
  ): Promise<void> {
    const sttId = getSttId(playbackKey)
    this.getOrCreateSourceState(sttId, getState, setState)

    const courtroomRealTimeSttConfigurationService = await firstValueFrom(
      this.courtroomRealTimeSttConfigurationService.get({ courtSystemId, locationId }).pipe(unwrapData()),
    )

    patchSttSourceState(setState, sttId, {
      courtroomSttMode: courtroomRealTimeSttConfigurationService.sttMode,
    })
  }

  @Action(FetchRealTimeSttCanSealContentAction)
  async fetchCanSealContent(
    { setState, getState }: StateContext<RealTimeSttStateModel>,
    { playbackKey, courtSystemId }: FetchRealTimeSttCanSealContentAction,
  ): Promise<void> {
    const sttId = getSttId(playbackKey)
    this.getOrCreateSourceState(sttId, getState, setState)

    // We only allow sealing on the recording playback page
    const canSealContent =
      playbackKey.type !== 'recording'
        ? false
        : await firstValueFrom(
            this.regionalPermissionService
              .hasPermission(courtSystemId, UserGroupPermissionId.SealContent, 'recording', [playbackKey.recordingId])
              .pipe(unwrapData()),
          )

    patchSttSourceState(setState, sttId, { canSealContent })
  }

  @Action(RealTimeSttSetSearchHighlightsCommand)
  setRemarkSearchHighlightContext(
    { setState, getState }: StateContext<RealTimeSttStateModel>,
    { playbackKey, searchHighlights }: RealTimeSttSetSearchHighlightsCommand,
  ): void {
    const sttId = getSttId(playbackKey)
    const originalState = getState().sttSourceState[sttId]
    if (!originalState || !originalState.segmentMap.isSuccess()) {
      // We can't update search highlighting for remarks which don't exist
      return
    }

    const originalSegmentMap = originalState.segmentMap._data
    const segmentMap = originalSegmentMap.rebuildWithUpdatedSearchHighlights(searchHighlights)

    patchSttSourceState(setState, sttId, { segmentMap: RemoteData.success(segmentMap) })
  }

  @Action(SetRealTimeSttFailedToSetupConnectionAction)
  async setFailedToSetupConnection(
    { setState, getState }: StateContext<RealTimeSttStateModel>,
    { playbackKey }: SetRealTimeSttFailedToSetupConnectionAction,
  ): Promise<void> {
    const sttId = getSttId(playbackKey)
    this.getOrCreateSourceState(sttId, getState, setState)

    patchSttSourceState(setState, sttId, { failedRealTimeSttConnection: true })
  }

  @Action(SetRealTimeSttHearingMarkersCommand)
  async setHearingMarkers(
    { setState, getState }: StateContext<RealTimeSttStateModel>,
    { playbackKey, hearingMarkers }: SetRealTimeSttHearingMarkersCommand,
  ): Promise<void> {
    const sttId = getSttId(playbackKey)
    const originalState = getState().sttSourceState[sttId]

    if (!originalState || !originalState.segmentMap.isSuccess()) {
      // We can't update hearing markers if we don't have a segment map
      return
    }
    const segmentMapWithHearingMarkers = originalState.segmentMap._data.rebuildWithHearingMarkers(hearingMarkers)

    patchSttSourceState(setState, sttId, { segmentMap: RemoteData.success(segmentMapWithHearingMarkers) })
  }

  @Action(SetRealTimeRecordingEndTimeCommand)
  async setRecordingEndTime(
    { setState, getState }: StateContext<RealTimeSttStateModel>,
    { playbackKey, recordingEndTime }: SetRealTimeRecordingEndTimeCommand,
  ): Promise<void> {
    const sttId = getSttId(playbackKey)
    this.getOrCreateSourceState(sttId, getState, setState)

    patchSttSourceState(setState, sttId, { recordingEndTime })
  }

  @Action(UpdateRealTimeLiveLocalTimeCommand)
  async updateLiveLocalTime(
    { setState, getState }: StateContext<RealTimeSttStateModel>,
    { playbackKey, liveLocalTime }: UpdateRealTimeLiveLocalTimeCommand,
  ): Promise<void> {
    const sttId = getSttId(playbackKey)
    this.getOrCreateSourceState(sttId, getState, setState)

    patchSttSourceState(setState, sttId, { liveLocalTime })
  }

  @Action(PerformSttEditOperationAction)
  async performSttEdit(
    { getState, setState, dispatch }: StateContext<RealTimeSttStateModel>,
    { playbackKey, courtSystemId, sttEditOperation }: PerformSttEditOperationAction,
  ): Promise<void> {
    const sttId = getSttId(playbackKey)
    this.getOrCreateSourceState(sttId, getState, setState)

    const segmentMapRemote = getState().sttSourceState[sttId]?.segmentMap

    if (!segmentMapRemote || !segmentMapRemote.isSuccess()) {
      // Can't edit if we don't have a segment map
      return
    }

    try {
      const operationResult = sttEditOperationToTransaction(sttEditOperation, segmentMapRemote._data)
      switch (operationResult.type) {
        case 'noChange':
          this.trackingService.track({
            event: 'SttEditOperationPerformed',
            editOperationType: sttEditOperation.type,
            editResultType: 'skipped',
            eventLabel: operationResult.reason,
          })
          dispatch(new SttEditOperationSkippedEvent(playbackKey, sttEditOperation, operationResult.reason))
          return
        case 'validationError':
          this.trackingService.track({
            event: 'SttEditOperationPerformed',
            editOperationType: sttEditOperation.type,
            editResultType: 'validation error',
            eventLabel: operationResult.message,
          })
          dispatch(new SttEditOperationFailedEvent(playbackKey, sttEditOperation, operationResult))
          if ('remarkId' in sttEditOperation)
            dispatch(new ShowValidationErrorRemarkCommand(playbackKey, sttEditOperation.remarkId, operationResult))

          break
        case 'transaction': {
          const { transaction, sideEffects } = operationResult
          await firstValueFrom(dispatch(new ApplySttEditTransactionAction(playbackKey, transaction)))
          dispatch(new PersistSttEditTransactionAction(playbackKey, courtSystemId, transaction))

          for (const sideEffect of sideEffects) {
            await firstValueFrom(dispatch(new InvokeSttEditOperationSideEffectAction(playbackKey, sideEffect)))
          }
          this.trackingService.track({
            event: 'SttEditOperationPerformed',
            editOperationType: sttEditOperation.type,
            editResultType: 'applied',
          })
          dispatch(
            new SttEditOperationSucceededEvent(
              playbackKey,
              sttEditOperation,
              transaction.edits.map(edit => edit.utteranceId),
            ),
          )
          break
        }
        default:
          assertUnreachable(operationResult)
      }
    } catch {
      this.trackingService.track({
        event: 'SttEditOperationPerformed',
        editOperationType: sttEditOperation.type,
        editResultType: 'applied',
      })
      dispatch(new SttEditOperationFailedEvent(playbackKey, sttEditOperation))
    }
  }

  @Action(ApplySttEditTransactionAction)
  async applySttEditTransaction(
    { setState, getState }: StateContext<RealTimeSttStateModel>,
    { playbackKey, transaction }: ApplySttEditTransactionAction,
  ): Promise<void> {
    const sttId = getSttId(playbackKey)
    this.getOrCreateSourceState(sttId, getState, setState)

    const sttSourceState = getState().sttSourceState[sttId]
    const segmentMapRemote = sttSourceState?.segmentMap

    if (!segmentMapRemote || !segmentMapRemote.isSuccess()) {
      // Can't apply transaction if we don't have a segment map
      return
    }

    // Use initial segment map to lookup speaker names (in case of series of operations that might remove speaker)
    const initialSegmentMap = segmentMapRemote._data
    let currentSegmentMap = initialSegmentMap
    for (const sttEdit of transaction.edits) {
      switch (sttEdit.type) {
        case 'upsertUtterance': {
          assert(sttEdit.fragments.length > 0, 'Fragments must not be empty for upsertUtterance')
          const speakerName = initialSegmentMap.findSpeakerNameById(sttEdit.speakerId)
          currentSegmentMap = currentSegmentMap.withUpsertedRemark({
            type: 'Remark',
            id: sttEdit.utteranceId,
            fragments: sttEdit.fragments,
            speakerName: speakerName ?? 'Unknown',
            speakerId: sttEdit.speakerId,
            dialect: sttEdit.dialect ?? undefined,
            liveStatus: 'final',
            // Computed from fragments
            content: sttEdit.fragments.map(f => f.content).join(' '),
            startTime: sttEdit.fragments[0].startTime,
            endTime: sttEdit.fragments[sttEdit.fragments.length - 1].endTime,
          })
          break
        }
        case 'deleteUtterance': {
          currentSegmentMap = currentSegmentMap.withDeletedRemark(sttEdit.utteranceId)
          break
        }
        case 'softCreateUtterance':
          {
            // This edit never overwrites existing remarks
            if (currentSegmentMap.findRemark(sttEdit.utteranceId)) {
              continue
            }
            const speakerName = initialSegmentMap.findSpeakerNameById(sttEdit.speakerId)
            currentSegmentMap = currentSegmentMap.withInsertedRemark({
              type: 'Remark',
              id: sttEdit.utteranceId,
              fragments: [],
              speakerName: speakerName ?? 'Unknown',
              speakerId: sttEdit.speakerId,
              dialect: sttEdit.dialect ?? undefined,
              content: '',
              startTime: sttEdit.startTime,
              endTime: sttEdit.endTime,
              liveStatus: 'final',
            })
          }
          break
        default:
          assertUnreachable(sttEdit)
      }
    }

    patchSttSourceState(setState, sttId, {
      segmentMap: RemoteData.success(currentSegmentMap),
    })
  }

  @Action(InvokeSttEditOperationSideEffectAction)
  async invokeSttEditOperationSideEffect(
    { dispatch }: StateContext<RealTimeSttStateModel>,
    { playbackKey, sideEffect }: InvokeSttEditOperationSideEffectAction,
  ): Promise<void> {
    switch (sideEffect.type) {
      case 'focusRemark':
        dispatch(new FocusEditableRemarkCommand(playbackKey, sideEffect.remarkId, sideEffect.selectionIndex))
        break
      case 'scrollToRemark':
        dispatch(new JumpRealTimeSttToTranscriptElementAction(playbackKey, sideEffect.remarkId, sideEffect.segmentId))
        break
      default:
        assertUnreachable(sideEffect)
    }
  }

  @Action(QueuePersistSttEditTransactionAction)
  async queuePersistSttEditTransaction(
    { getState, dispatch, setState }: StateContext<RealTimeSttStateModel>,
    { courtSystemId, playbackKey, transaction }: QueuePersistSttEditTransactionAction,
  ): Promise<void> {
    const sttId = getSttId(playbackKey)
    this.getOrCreateSourceState(sttId, getState, setState)

    const persistQueue = getState().sttSourceState[sttId]?.editPersistQueue
    assert(persistQueue, 'persist queue must be setup for sttId')
    patchSttSourceState(setState, sttId, {
      editPersistQueue: patch({
        queuedJobs: [
          ...persistQueue.queuedJobs,
          {
            courtSystemId,
            transaction,
          },
        ],
      }),
    })

    dispatch(new TriggerSttEditPersistQueueProcessingAction(playbackKey))
  }

  @Action(TriggerSttEditPersistQueueProcessingAction)
  async triggerSttEditPersistQueueProcessing(
    { getState, dispatch, setState }: StateContext<RealTimeSttStateModel>,
    { playbackKey }: TriggerSttEditPersistQueueProcessingAction,
  ): Promise<void> {
    const sttId = getSttId(playbackKey)
    this.getOrCreateSourceState(sttId, getState, setState)

    const persistQueue = getState().sttSourceState[sttId]?.editPersistQueue
    assert(persistQueue, 'persist queue must be setup for sttId')

    if (persistQueue.persistInProgress || persistQueue.queuedJobs.length === 0) {
      return
    }

    const [jobToRun, ...remainingQueuedJobs] = persistQueue.queuedJobs
    patchSttSourceState(setState, sttId, {
      editPersistQueue: {
        queuedJobs: remainingQueuedJobs,
        persistInProgress: true,
      },
    })

    await firstValueFrom(
      dispatch(new PersistSttEditTransactionAction(playbackKey, jobToRun.courtSystemId, jobToRun.transaction)),
    )

    patchSttSourceState(setState, sttId, {
      editPersistQueue: patch({
        persistInProgress: false,
      }),
    })

    dispatch(new TriggerSttEditPersistQueueProcessingAction(playbackKey))
  }

  @Action(PersistSttEditTransactionAction)
  async persistSttEditTransaction(
    { getState, setState, dispatch }: StateContext<RealTimeSttStateModel>,
    { courtSystemId, playbackKey, transaction }: PersistSttEditTransactionAction,
  ): Promise<void> {
    const sttId = getSttId(playbackKey)
    this.getOrCreateSourceState(sttId, getState, setState)

    if (playbackKey.type !== 'recording') {
      throw new Error('Editing is only available for recordings')
    }
    dispatch(new PersistSttEditTransactionBeganEvent(playbackKey, transaction, courtSystemId))

    try {
      await lastValueFrom(
        this.realTimeCourtRecordingSttService
          .sttEdit(courtSystemId, playbackKey.recordingId, transaction)
          .pipe(unwrapData()),
      )
      dispatch(new PersistSttEditTransactionSucceededEvent(playbackKey, transaction, courtSystemId))
    } catch {
      dispatch(new PersistSttEditTransactionFailedEvent(playbackKey, transaction, courtSystemId))
    }
  }

  private getOrCreateSourceState(
    sttId: Uuid,
    getState: () => RealTimeSttStateModel,
    setState: StateContext<RealTimeSttStateModel>['setState'],
  ): RealTimeSttSourceStateModel {
    let state = getState().sttSourceState[sttId]
    if (!state) {
      state = defaultSourceState()
      setState(
        patch<RealTimeSttStateModel>({
          sttSourceState: patch({
            [sttId]: state,
          }),
        }),
      )
    }
    return state
  }

  private getRemarks(
    playbackKey: RealTimePlaybackKey,
    courtSystemId: Uuid,
    canReadStt: boolean,
  ): ApiResult<RealTimeSttWithRemarks, Error> {
    let remarks$: ApiResult<RealTimeSttWithRemarks>
    switch (playbackKey.type) {
      case 'recording':
        remarks$ = this.realTimeCourtRecordingSttService.getSttWithRemarks(
          courtSystemId,
          playbackKey.recordingId,
          canReadStt,
        )
        break
      case 'audio-segment':
        remarks$ = this.realTimeAudioSegmentSttService.getAudioSegmentWithSttRemarks(
          courtSystemId,
          playbackKey.audioSegmentId,
          canReadStt,
        )
        break
      case 'hearing':
        remarks$ = this.regionalHearingsService.getHearingWithSttRemarks(courtSystemId, playbackKey.hearingId)
        break
      case 'hearing-section':
        remarks$ = this.regionalHearingsService.getHearingSectionWithSttRemarks(
          courtSystemId,
          playbackKey.hearingSectionId,
        )
        break
      default:
        assertUnreachable(playbackKey)
    }

    return remarks$
  }
}

function patchSttSourceState(
  setState: StateContext<RealTimeSttStateModel>['setState'],
  sttId: Uuid,
  update: ɵPatchSpec<RealTimeSttSourceStateModel>,
): void {
  setState(
    patch<RealTimeSttStateModel>({
      sttSourceState: patch({
        [sttId]: patch(update),
      }),
    }),
  )
}

function getSttId(key: RealTimePlaybackKey): Uuid {
  switch (key.type) {
    case 'recording':
      return key.recordingId
    case 'audio-segment':
      return key.audioSegmentId
    case 'hearing':
      return key.hearingId
    case 'hearing-section':
      return key.hearingSectionId
    default:
      assertUnreachable(key)
  }
}

function createMemoizedSttSourceStateSelector<T extends keyof RealTimeSttSourceStateModel>(
  property: T,
): (
  playbackKey: RealTimePlaybackKey,
) => (sourceStates: RealTimeSttSourceStateModel | undefined) => RealTimeSttSourceStateModel[T] {
  return memoize(
    (
      playbackKey: RealTimePlaybackKey,
    ): ((sourceStates: RealTimeSttSourceStateModel | undefined) => RealTimeSttSourceStateModel[T]) => {
      const selectedSttSourceState = RealTimeSttState.sttSourceState(playbackKey)
      return createSelector([selectedSttSourceState], (state: ReturnType<typeof selectedSttSourceState>) => {
        return state?.[property] ?? defaultSourceState()[property]
      })
    },
    playbackKey => getSttId(playbackKey),
  )
}
